/**
 * The desktop application's text normalisation for the school and district fields, ported
 * rule for rule. `MainWindow.xaml.cs` is the reference: drafters moving between the two expect
 * "Wilson ES" to become "Wilson Elementary School" in both.
 */

const SCHOOL_TYPES: Readonly<Record<string, string>> = {
  ES: "Elementary School",
  MS: "Middle School",
  HS: "High School",
  JHS: "Junior High School",
  PS: "Primary School",
};

const DISTRICT_WORDS: Readonly<Record<string, string>> = {
  CO: "County", CNTY: "County", CTY: "County",
  DIST: "District", CONS: "Consolidated", REG: "Regional",
  TWP: "Township", ELEM: "Elementary", JR: "Junior", SR: "Senior",
  ACAD: "Academy", VOC: "Vocational", MT: "Mount", FT: "Fort",
  HTS: "Heights", JT: "Joint", ST: "Saint", SD: "School District",
  DIV: "Division",
};

const STATES: Readonly<Record<string, string>> = {
  "ALABAMA": "AL", "ALASKA": "AK", "ARIZONA": "AZ", "ARKANSAS": "AR",
  "CALIFORNIA": "CA", "COLORADO": "CO", "CONNECTICUT": "CT", "DELAWARE": "DE",
  "FLORIDA": "FL", "GEORGIA": "GA", "HAWAII": "HI", "IDAHO": "ID",
  "ILLINOIS": "IL", "INDIANA": "IN", "IOWA": "IA", "KANSAS": "KS",
  "KENTUCKY": "KY", "LOUISIANA": "LA", "MAINE": "ME", "MARYLAND": "MD",
  "MASSACHUSETTS": "MA", "MICHIGAN": "MI", "MINNESOTA": "MN", "MISSISSIPPI": "MS",
  "MISSOURI": "MO", "MONTANA": "MT", "NEBRASKA": "NE", "NEVADA": "NV",
  "NEW HAMPSHIRE": "NH", "NEW JERSEY": "NJ", "NEW MEXICO": "NM", "NEW YORK": "NY",
  "NORTH CAROLINA": "NC", "NORTH DAKOTA": "ND", "OHIO": "OH", "OKLAHOMA": "OK",
  "OREGON": "OR", "PENNSYLVANIA": "PA", "RHODE ISLAND": "RI", "SOUTH CAROLINA": "SC",
  "SOUTH DAKOTA": "SD", "TENNESSEE": "TN", "TEXAS": "TX", "UTAH": "UT",
  "VERMONT": "VT", "VIRGINIA": "VA", "WASHINGTON": "WA", "WEST VIRGINIA": "WV",
  "WISCONSIN": "WI", "WYOMING": "WY", "DISTRICT OF COLUMBIA": "DC",
  "PUERTO RICO": "PR", "GUAM": "GU", "AMERICAN SAMOA": "AS",
  "U.S. VIRGIN ISLANDS": "VI", "VIRGIN ISLANDS": "VI",
  "NORTHERN MARIANA ISLANDS": "MP",
};

const VALID_STATE_CODES: ReadonlySet<string> = new Set(Object.values(STATES));

/** A full name or an abbreviation in any case becomes the USPS code; anything else is uppercased. */
export function normalizeState(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === "") return "";
  const upper = trimmed.toUpperCase();
  if (trimmed.length === 2 && VALID_STATE_CODES.has(upper)) return upper;
  return STATES[upper] ?? upper;
}

export function expandSchoolTypeAbbreviations(text: string): string {
  return text.replace(
    /\b(ES|MS|HS|JHS|PS)\b\.?/gi,
    (match, abbreviation: string) => SCHOOL_TYPES[abbreviation.toUpperCase()] ?? match,
  );
}

/**
 * Low-ambiguity words always expand. ISD, CSD, USD, CUSD and CCSD depend on the state: in
 * several states the abbreviation is already the district's legal name, so expanding it would
 * change a proper noun. The per-state exceptions are the desktop application's.
 */
export function expandDistrictAbbreviations(text: string, stateRaw: string): string {
  if (text.trim() === "") return text;
  const state = normalizeState(stateRaw);

  const words = text.replace(
    /\b(CO|CNTY|CTY|DIST|CONS|REG|TWP|ELEM|JR|SR|ACAD|VOC|MT|FT|HTS|JT|ST|SD|DIV)\b\.?/gi,
    (match, word: string) => DISTRICT_WORDS[word.toUpperCase()] ?? match,
  );

  return words.replace(/\b(ISD|CSD|USD|CUSD|CCSD)\b\.?/gi, (match, abbreviation: string) => {
    switch (abbreviation.toUpperCase()) {
      case "ISD":
        return state === "TX" ? match : "Independent School District";
      case "CSD":
        return state === "NY" ? "Central School District" : "County School District";
      case "USD":
        return state === "KS" ? match : "Unified School District";
      case "CUSD":
        return state === "IL" ? "Community Unit School District" : match;
      case "CCSD":
        return state === "NV" || state === "" ? match : "Community Consolidated School District";
      default:
        return match;
    }
  });
}

/** What the address looks like it lacks, by the desktop's comma-segment heuristic. Empty means fine. */
export function addressGaps(address: string): readonly string[] {
  const trimmed = address.trim();
  if (trimmed === "") return [];
  const segments = trimmed.split(",").map((s) => s.trim()).filter((s) => s !== "");
  const hasZip = /\b\d{5}(-\d{4})?\b/.test(trimmed);
  const hasState = segments.some((segment) =>
    segment.split(/\s+/).some((word) => {
      const candidate = word.trim().replace(/[.,]+$/, "");
      return candidate.length === 2 && VALID_STATE_CODES.has(candidate.toUpperCase());
    }),
  );
  const missing: string[] = [];
  if (segments.length < 3) missing.push("city");
  if (!hasState) missing.push("state");
  if (!hasZip) missing.push("ZIP code");
  return missing;
}

/** A Portal ID is six digits; an empty one is not wrong, only absent. */
export function portalIdIsInvalid(portalId: string): boolean {
  if (portalId === "") return false;
  return [...portalId].filter((c) => c >= "0" && c <= "9").length !== 6;
}

/** " - Complete Map" is how drafters name the source file; the school name is what is left. */
export function schoolNameFromFileName(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  const stem = dot > 0 ? fileName.slice(0, dot) : fileName;
  return stem.replace(/ - Complete Map/gi, "").trim();
}
